// src/errors.ts
var ErrorCode = {
  /** 参数/分页/locale 非法（400） */
  BAD_REQUEST: "BAD_REQUEST",
  /** 未认证（401） */
  UNAUTHORIZED: "UNAUTHORIZED",
  /** 无权限（403） */
  FORBIDDEN: "FORBIDDEN",
  /** 内容不存在、未发布或已下线（404）——公开端草稿/下线一律 404，不泄露存在性 */
  NOT_FOUND: "NOT_FOUND",
  /** 请求体冲突（409），如 slug 重复 */
  CONFLICT: "CONFLICT",
  /** 表单/问卷字段无效（422） */
  VALIDATION_FAILED: "VALIDATION_FAILED",
  /** 上传超限或类型不在白名单（413/422） */
  PAYLOAD_TOO_LARGE: "PAYLOAD_TOO_LARGE",
  /** 验证码（阿里云验证码 2.0）、honeypot 或频率限制失败（429） */
  RATE_LIMITED: "RATE_LIMITED",
  /** 服务内部错误（500）——message 固定为通用话术，不含内部详情 */
  INTERNAL: "INTERNAL_ERROR"
};
var statusToErrorCode = {
  400: ErrorCode.BAD_REQUEST,
  401: ErrorCode.UNAUTHORIZED,
  403: ErrorCode.FORBIDDEN,
  404: ErrorCode.NOT_FOUND,
  409: ErrorCode.CONFLICT,
  413: ErrorCode.PAYLOAD_TOO_LARGE,
  422: ErrorCode.VALIDATION_FAILED,
  429: ErrorCode.RATE_LIMITED,
  500: ErrorCode.INTERNAL
};

// src/events.ts
var EventName = {
  town_viewed: "town_viewed",
  building_opened: "building_opened",
  builder_card_opened: "builder_card_opened",
  builders_dial_viewed: "builders_dial_viewed",
  builder_switched: "builder_switched",
  notice_board_viewed: "notice_board_viewed",
  notice_era_changed: "notice_era_changed",
  notice_opened: "notice_opened",
  language_changed: "language_changed",
  contact_letter_started: "contact_letter_started",
  contact_letter_completed: "contact_letter_completed",
  contact_letter_failed: "contact_letter_failed",
  welcome_letter_shown: "welcome_letter_shown",
  welcome_letter_opened: "welcome_letter_opened",
  welcome_letter_dismissed: "welcome_letter_dismissed"
};
var eventPropertiesWhitelist = {
  town_viewed: ["locale", "theme", "device", "degraded"],
  building_opened: ["entranceKey", "source", "locale"],
  builder_card_opened: ["builderSlug", "source", "locale"],
  builders_dial_viewed: ["locale", "count"],
  builder_switched: ["builderSlug", "method", "locale"],
  notice_board_viewed: ["locale", "eraKey"],
  notice_era_changed: ["eraKey", "method", "locale"],
  notice_opened: ["noticeSlug", "source", "locale"],
  language_changed: ["from", "to", "path"],
  contact_letter_started: ["contactType", "locale"],
  contact_letter_completed: ["contactType", "locale"],
  contact_letter_failed: ["contactType", "reason", "locale"],
  welcome_letter_shown: ["locale"],
  welcome_letter_opened: ["locale"],
  welcome_letter_dismissed: ["method", "locale"]
};

// src/contact.ts
import { z } from "zod";
var localeSchema = z.enum(["zh-CN", "en"]);
var contactTypeSchema = z.enum(["creators", "partners"]);
var stepTypeSchema = z.enum(["text", "email", "url", "chips", "textarea", "file"]);
var attachmentAcceptExtensions = [
  ".pdf",
  ".mp4",
  ".mov",
  ".mp3",
  ".wav",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".doc",
  ".docx",
  ".key",
  ".ppt",
  ".pptx"
];
var attachmentMaxBytes = 50 * 1024 * 1024;
var conversationStepSchema = z.object({
  key: z.string().min(1).max(64),
  type: stepTypeSchema,
  required: z.boolean(),
  q: z.object({ zh: z.string(), en: z.string() }),
  ph: z.object({ zh: z.string(), en: z.string() }).optional(),
  chips: z.array(z.object({ zh: z.string(), en: z.string() })).optional(),
  accept: z.array(z.string()).optional(),
  invalid: z.object({ zh: z.string(), en: z.string() }).optional(),
  skippable: z.boolean().optional()
});
var conversationScriptSchema = z.object({
  contactType: contactTypeSchema,
  intro: z.object({ zh: z.string(), en: z.string() }),
  steps: z.array(conversationStepSchema).min(1).max(24),
  paper: z.object({ zh: z.string(), en: z.string() })
});
var contactSubmissionPayloadSchema = z.object({
  type: contactTypeSchema,
  locale: localeSchema,
  /** 对话答案：key 与剧本 steps[].key 同名 */
  answers: z.record(z.string(), z.string().max(5e3))
  /** file 题若上传了附件：`<key>AssetKey` → 对象存储 key（由直传通道颁发） */
}).strict();
var honeypotFieldName = "website";
var captchaTicketFieldName = "captchaTicket";
function assetKeyFieldName(stepKey) {
  return `${stepKey}AssetKey`;
}

// src/contact-scripts.ts
var conversationScripts = {
  creators: {
    contactType: "creators",
    intro: {
      zh: "\u4F60\u597D\uFF0C\u6211\u662F\u9547\u4E0A\u7684\u4FE1\u4F7F\u3002\u542C\u8BF4\u4F60\u60F3\u628A\u521B\u4F5C\u9012\u8FDB\u5C0F\u9547\uFF1F\u5148\u544A\u8BC9\u6211\u600E\u4E48\u79F0\u547C\u4F60\u3002",
      en: "Hello, I'm the town messenger. You'd like to bring your practice into the town? First \u2014 what should I call you?"
    },
    steps: [
      {
        key: "name",
        type: "text",
        required: true,
        q: { zh: "\u600E\u4E48\u79F0\u547C\u4F60\uFF1F", en: "What's your name?" },
        ph: { zh: "\u4F60\u7684\u540D\u5B57\u6216\u6635\u79F0", en: "Your name or nickname" }
      },
      {
        key: "email",
        type: "email",
        required: true,
        q: { zh: "\u4FE1\u9001\u5230\u54EA\u91CC\uFF1F\u7559\u4E00\u4E2A\u90AE\u7BB1\uFF0C\u56DE\u4FE1\u4F1A\u5BC4\u5230\u90A3\u91CC\u3002", en: "Where should the reply go? Leave an email address." },
        ph: { zh: "you@example.com", en: "you@example.com" },
        invalid: {
          zh: "\u8FD9\u4E2A\u5730\u5740\u597D\u50CF\u5BC4\u4E0D\u5230\uFF0C\u518D\u770B\u770B\uFF1F",
          en: "That address doesn't look deliverable \u2014 mind checking it?"
        }
      },
      {
        key: "location",
        type: "text",
        required: false,
        q: { zh: "\u4F60\u5E38\u5728\u54EA\u4E2A\u57CE\u5E02\uFF1F\u4E0D\u77E5\u9053\u5199\u4EC0\u4E48\u5C31\u8DF3\u8FC7\u3002", en: "Which city do you usually work from? Skip if you'd rather not say." },
        ph: { zh: "\u6240\u5728\u5730\uFF08\u53EF\u8DF3\u8FC7\uFF09", en: "City (optional)" }
      },
      {
        key: "focus",
        type: "chips",
        required: true,
        q: { zh: "\u4F60\u5728\u505A\u54EA\u4E00\u7C7B\u521B\u4F5C\uFF1F\u70B9\u4E00\u4E0B\uFF0C\u6216\u5728\u4E0B\u9762\u8865\u4E00\u53E5\u3002", en: "What kind of work do you make? Tap one, or add your own below." },
        chips: [
          { zh: "\u5F71\u50CF", en: "Visual" },
          { zh: "\u58F0\u97F3", en: "Sound" },
          { zh: "\u5199\u4F5C", en: "Writing" },
          { zh: "\u5236\u4F5C", en: "Production" },
          { zh: "\u7A7A\u95F4", en: "Spatial" },
          { zh: "\u5176\u4ED6", en: "Other" }
        ],
        ph: { zh: "\u6216\u8005\u7528\u81EA\u5DF1\u7684\u8BDD\u8BF4", en: "Or describe it in your own words" }
      },
      {
        key: "link",
        type: "file",
        required: true,
        q: { zh: "\u7ED9\u6211\u770B\u770B\u4F60\u7684\u4F5C\u54C1\uFF1FPDF\u3001\u89C6\u9891\u3001\u97F3\u9891\u90FD\u884C\uFF0C\u6216\u8005\u8D34\u4E00\u4E2A\u94FE\u63A5\u3002", en: "Show me your work \u2014 PDF, video, audio, or a link." },
        accept: [".pdf", ".mp4", ".mov", ".mp3", ".wav", ".png", ".jpg", ".jpeg", ".webp", ".doc", ".docx", ".key", ".ppt", ".pptx"],
        ph: { zh: "\u6216\u7C98\u8D34\u4F5C\u54C1\u94FE\u63A5", en: "Or paste a portfolio link" },
        invalid: {
          zh: "\u8FD9\u4E2A\u94FE\u63A5\u597D\u50CF\u6253\u4E0D\u5F00\uFF0C\u518D\u68C0\u67E5\u4E00\u4E0B\uFF1F",
          en: "That link doesn't look right \u2014 could you check it?"
        }
      },
      {
        key: "message",
        type: "textarea",
        required: true,
        q: { zh: "\u6700\u540E\uFF0C\u60F3\u5BF9\u5C0F\u9547\u8BF4\u7684\u8BDD\u3002\u4FE1\u4F7F\u4F1A\u4E00\u5B57\u4E0D\u843D\u5730\u5E26\u5230\u3002", en: "Lastly, your words to the town. I'll carry them word for word." },
        ph: { zh: "\u60F3\u8BF4\u7684\u90FD\u5199\u5728\u8FD9\u91CC\u2026\u2026", en: "Write anything you'd like to say\u2026" }
      }
    ],
    paper: { zh: "\u521B\u4F5C\u8005\u7684\u4FE1", en: "A letter from a creator" }
  },
  partners: {
    contactType: "partners",
    intro: {
      zh: "\u4F60\u597D\uFF0C\u6211\u662F\u9547\u4E0A\u7684\u4FE1\u4F7F\u3002\u516C\u53F8\u7684\u4E8B\u8981\u529E\u5F97\u59A5\u5F53\uFF0C\u6211\u591A\u95EE\u51E0\u53E5\uFF0C\u522B\u5ACC\u70E6\u3002",
      en: "Hello, I'm the town messenger. Company matters deserve care, so I'll ask a few more questions."
    },
    steps: [
      {
        key: "name",
        type: "text",
        required: true,
        q: { zh: "\u600E\u4E48\u79F0\u547C\u4F60\uFF1F", en: "What's your name?" },
        ph: { zh: "\u4F60\u7684\u540D\u5B57", en: "Your name" }
      },
      {
        key: "company",
        type: "text",
        required: true,
        q: { zh: "\u4F60\u4EE3\u8868\u54EA\u5BB6\u516C\u53F8\uFF1F", en: "Which company do you represent?" },
        ph: { zh: "\u516C\u53F8\u540D\u79F0", en: "Company name" }
      },
      {
        key: "title",
        type: "text",
        required: false,
        q: { zh: "\u4F60\u7684\u804C\u4F4D\u662F\uFF1F\uFF08\u53EF\u4EE5\u8DF3\u8FC7\uFF09", en: "Your title? (optional)" },
        ph: { zh: "\u804C\u4F4D\uFF08\u53EF\u8DF3\u8FC7\uFF09", en: "Title (optional)" }
      },
      {
        key: "email",
        type: "email",
        required: true,
        q: { zh: "\u5DE5\u4F5C\u90AE\u7BB1\u7559\u4E00\u4E2A\uFF0C\u56DE\u4FE1\u5BC4\u5230\u90A3\u91CC\u3002", en: "Leave a work email \u2014 the reply will go there." },
        ph: { zh: "name@company.com", en: "name@company.com" },
        invalid: {
          zh: "\u8FD9\u4E2A\u5730\u5740\u597D\u50CF\u5BC4\u4E0D\u5230\uFF0C\u518D\u770B\u770B\uFF1F",
          en: "That address doesn't look deliverable \u2014 mind checking it?"
        }
      },
      {
        key: "type",
        type: "chips",
        required: true,
        q: { zh: "\u8FD9\u6B21\u60F3\u8C08\u54EA\u4E00\u7C7B\u5408\u4F5C\uFF1F", en: "What kind of collaboration is this?" },
        chips: [
          { zh: "\u5185\u5BB9\u5408\u4F5C", en: "Content" },
          { zh: "\u54C1\u724C\u5408\u4F5C", en: "Brand" },
          { zh: "\u6280\u672F\u4E0E\u4EA7\u54C1", en: "Technology & Product" },
          { zh: "\u5176\u4ED6", en: "Other" }
        ],
        ph: { zh: "\u6216\u8005\u7528\u81EA\u5DF1\u7684\u8BDD\u8BF4", en: "Or describe it in your own words" }
      },
      {
        key: "brief",
        type: "textarea",
        required: true,
        q: { zh: "\u7B80\u5355\u8BB2\u8BB2\u8FD9\u6B21\u5408\u4F5C\u7684\u6837\u5B50\u3002", en: "Tell me briefly what this collaboration looks like." },
        ph: { zh: "\u9879\u76EE\u7B80\u4ECB\u2026\u2026", en: "Project brief\u2026" }
      },
      {
        key: "attachment",
        type: "file",
        required: false,
        q: { zh: "\u6709\u8D44\u6599\u8981\u4E00\u8D77\u5E26\u5417\uFF1F\u65B9\u6848\u3001\u753B\u518C\u90FD\u53EF\u4EE5\u9644\u4E0A\uFF0C\u6CA1\u6709\u5C31\u8DF3\u8FC7\u3002", en: "Anything to enclose? Attach a brief or brochure \u2014 skip if not." },
        accept: [".pdf", ".mp4", ".mov", ".mp3", ".wav", ".png", ".jpg", ".jpeg", ".webp", ".doc", ".docx", ".key", ".ppt", ".pptx"],
        ph: { zh: "\u6216\u7C98\u8D34\u8D44\u6599\u94FE\u63A5", en: "Or paste a link" },
        invalid: {
          zh: "\u8FD9\u4E2A\u94FE\u63A5\u597D\u50CF\u6253\u4E0D\u5F00\uFF0C\u518D\u68C0\u67E5\u4E00\u4E0B\uFF1F",
          en: "That link doesn't look right \u2014 could you check it?"
        },
        skippable: true
      },
      {
        key: "budget",
        type: "chips",
        required: false,
        q: { zh: "\u9884\u7B97\u8303\u56F4\u65B9\u4FBF\u8BF4\u5417\uFF1F\u4E0D\u65B9\u4FBF\u5C31\u8DF3\u8FC7\u3002", en: "Mind sharing a budget range? Skip if not." },
        chips: [
          { zh: "\u6682\u4E0D\u786E\u5B9A", en: "Not sure yet" },
          { zh: "10 \u4E07\u4EE5\u5185", en: "Under \xA5100k" },
          { zh: "10\u201350 \u4E07", en: "\xA5100k\u2013500k" },
          { zh: "50\u2013100 \u4E07", en: "\xA5500k\u20131M" },
          { zh: "100 \u4E07\u4EE5\u4E0A", en: "Over \xA51M" }
        ],
        skippable: true
      },
      {
        key: "message",
        type: "textarea",
        required: false,
        q: { zh: "\u8FD8\u6709\u4EC0\u4E48\u60F3\u8865\u5145\u7684\uFF1F\u6CA1\u6709\u5C31\u8BF4\u300C\u6CA1\u6709\u4E86\u300D\u3002", en: "Anything to add? If not, just say so." },
        ph: { zh: "\u8865\u5145\u7559\u8A00\uFF08\u53EF\u8DF3\u8FC7\uFF09", en: "Additional notes (optional)" },
        skippable: true
      }
    ],
    paper: { zh: "\u5408\u4F5C\u4F19\u4F34\u7684\u4FE1", en: "A letter from a partner" }
  }
};
var paperShortLabels = {
  creators: {
    name: { zh: "\u7F72\u540D", en: "Name" },
    email: { zh: "\u56DE\u4FE1\u5730\u5740", en: "Reply to" },
    location: { zh: "\u6240\u5728\u57CE\u5E02", en: "City" },
    focus: { zh: "\u521B\u4F5C\u65B9\u5411", en: "Focus" },
    link: { zh: "\u4F5C\u54C1", en: "Works" },
    message: { zh: "\u60F3\u8BF4\u7684\u8BDD", en: "Words" }
  },
  partners: {
    name: { zh: "\u7F72\u540D", en: "Name" },
    company: { zh: "\u516C\u53F8", en: "Company" },
    title: { zh: "\u804C\u4F4D", en: "Title" },
    email: { zh: "\u56DE\u4FE1\u5730\u5740", en: "Reply to" },
    type: { zh: "\u5408\u4F5C\u7C7B\u578B", en: "Type" },
    brief: { zh: "\u9879\u76EE\u7B80\u4ECB", en: "Brief" },
    attachment: { zh: "\u9644\u4EF6", en: "Enclosure" },
    budget: { zh: "\u9884\u7B97", en: "Budget" },
    message: { zh: "\u60F3\u8BF4\u7684\u8BDD", en: "Words" }
  }
};
var consentVersion = "2026-09-16";

// src/builders.ts
var socialPlatforms = ["wechat", "xiaohongshu", "shipinhao", "douyin", "bilibili", "other"];

// src/content.ts
var ContentStatus = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
  OFFLINE: "OFFLINE"
};

// src/admin-content.ts
var noticeTones = ["cream", "manila", "grey"];

// src/assets.ts
var assetPurposes = ["builder-photo", "contact-attachment", "social-qr"];

// src/settings.ts
var settingKeys = [
  "site.defaultLocale",
  "site.theme",
  "site.socialLinks",
  "builders.sortMode"
];
var builderSortModes = ["manual", "name_asc", "name_desc"];

// src/api-keys.ts
var apiKeyScopeDomains = [
  "builders",
  "notices",
  "submissions",
  "pages",
  "entrances",
  "settings",
  "town",
  "assets",
  "analytics"
];
var apiKeyPresets = {
  readonly: {
    label: "\u53EA\u8BFB\u5DE1\u68C0",
    scopes: {
      builders: "read",
      notices: "read",
      submissions: "read",
      pages: "read",
      entrances: "read",
      settings: "read",
      town: "read",
      assets: "read",
      analytics: "read"
    }
  },
  content: {
    label: "\u5185\u5BB9\u7BA1\u7406",
    scopes: {
      builders: "write",
      notices: "write",
      submissions: "read",
      pages: "write",
      entrances: "write",
      settings: "read",
      town: "write",
      assets: "write",
      analytics: "read"
    }
  }
};
export {
  ContentStatus,
  ErrorCode,
  EventName,
  apiKeyPresets,
  apiKeyScopeDomains,
  assetKeyFieldName,
  assetPurposes,
  attachmentAcceptExtensions,
  attachmentMaxBytes,
  builderSortModes,
  captchaTicketFieldName,
  consentVersion,
  contactSubmissionPayloadSchema,
  contactTypeSchema,
  conversationScriptSchema,
  conversationScripts,
  conversationStepSchema,
  eventPropertiesWhitelist,
  honeypotFieldName,
  localeSchema,
  noticeTones,
  paperShortLabels,
  settingKeys,
  socialPlatforms,
  statusToErrorCode,
  stepTypeSchema
};
